import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, GraduationCap, Wrench, MapPin, HeartPulse, Plus, Check, ArrowUpRight } from "lucide-react";
import ImageSlot from "@/components/ImageSlot";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "@/components/Reveal";
import { ToothMark } from "@/components/Logo";
import { TeamMemberDialog } from "@/components/TeamMemberDialog";
import { IMAGES, SERVICES } from "@/data/site";

const MARIO_FOCUS = [
    "Odontoiatria conservativa",
    "Endodonzia",
    "Chirurgia orale",
    "Implantologia",
    "Protesi fissa e mobile",
    "Gnatologia",
    "Provvisori e ritocchi realizzati direttamente in studio",
    "Prima visita, diagnosi e piano di cura con TAC 3D",
    "Estetica del sorriso",
];
const MARIO_SERVICES = ["conservativa", "endodonzia", "chirurgia-orale", "implantologia", "protesi-laboratorio", "gnatologia", "estetica"];

const TEAM_ALL = [
    {
        name: "Dott.ssa Alice Massironi",
        role: "Odontoiatra",
        bio: "Laureata presso l'Università di Milano-Bicocca, cura i percorsi di salute orale e prevenzione dello studio, con un approccio delicato e privo di stress.",
        details: [
            "Laureata presso l'Università di Milano-Bicocca, cura i percorsi di salute orale e prevenzione dello studio. Segue il paziente in ogni fase del suo percorso clinico, dai controlli periodici ai trattamenti di routine, garantendo un approccio delicato, attento alle esigenze individuali e privo di stress.",
        ],
        focus: [
            "Igiene",
            "Pedodonzia",
            "Prime visite e controlli",
            "Conservativa",
            "Endodonzia",
            "Chirurgia",
        ],
        services: ["igiene-prevenzione", "pedodonzia", "conservativa", "endodonzia", "chirurgia-orale"],
        photo: "/images/alice.webp",
        testid: "team-alice",
    },
    {
        name: "Dr. Carlo Cantù",
        role: "Ortodontista",
        bio: "Ortodontista esclusivista dal 1986, laureato alla Statale di Milano. Già responsabile di Ortodonzia per le ASL della Brianza e direttore di Pentadent Swiss: decine di migliaia di casi trattati tra Lombardia e Canton Ticino.",
        details: [
            "Laureato all'Università Statale di Milano, il Dr. Cantù si dedica esclusivamente all'ortodonzia dal 1986. È stato responsabile di Ortodonzia per le ASL della Brianza e direttore di Pentadent Swiss, in Canton Ticino.",
            "In oltre trentacinque anni ha seguito decine di migliaia di casi, dai bambini agli adulti. Nel nostro studio segue ogni piano di trattamento sul viso e sui tempi del paziente, scegliendo la soluzione più discreta ed efficace.",
        ],
        focus: [
            "Ortodonzia invisibile con allineatori trasparenti",
            "Apparecchi tradizionali per i casi complessi",
            "Ortodonzia intercettiva nei bambini",
            "Gnatologia",
            "Trattamenti per adulti e ortodonzia pre-protesica",
        ],
        services: ["ortodonzia", "gnatologia", "pedodonzia"],
        photo: "/images/cantu.webp",
        photos: ["/images/cantu.webp", "/images/cantu-2.webp"],
        testid: "team-cantu",
    },
    {
        name: "Dr. Alessandro Amici",
        role: "Chirurgo Maxillo-Facciale",
        bio: "Medico chirurgo specialista in Chirurgia Maxillo-Facciale, con Master in Microchirurgia e Chirurgia ricostruttiva oncologica. Chirurgo al Policlinico di Monza: in studio esegue implantologia, chirurgia orale e biopsie.",
        details: [
            "Medico chirurgo specialista in Chirurgia Maxillo-Facciale, con Master in Microchirurgia e in Chirurgia ricostruttiva oncologica, il Dr. Amici opera al Policlinico di Monza.",
            "Nel nostro studio porta l'esperienza ospedaliera nella chirurgia di tutti i giorni: interventi pianificati con la TAC 3D, protocolli delicati e un'assistenza diretta nei giorni successivi.",
        ],
        focus: [
            "Estrazioni complesse e denti del giudizio inclusi",
            "Rigenerativa",
            "Implantologia",
            "Biopsia e diagnosi delle lesioni del cavo orale",
        ],
        services: ["implantologia", "chirurgia-orale", "patologia"],
        photo: "/images/amici.webp",
        photos: ["/images/amici.webp", "/images/amici-2.webp"],
        testid: "team-amici",
    },
    {
        name: "Accoglienza & Segreteria",
        role: "Il primo sorriso che incontri",
        bio: "La figura centrale per l'accoglienza e la gestione organizzativa dello studio: con empatia e disponibilità, accompagna i pazienti in ogni fase del loro percorso.",
        details: [
            "La figura centrale per l'accoglienza e la gestione organizzativa dello studio. Con empatia e disponibilità, accompagna i pazienti in ogni fase del loro percorso, occupandosi della prenotazione delle visite, dell'assistenza amministrativa e di garantire un ambiente sempre sereno e ben strutturato.",
        ],
        focus: [
            "Prenotazione delle visite, richiami e urgenze",
            "Accoglienza e gestione della sala d'attesa",
            "Assistenza amministrativa",
            "Preventivi, pagamenti e documenti",
        ],
        services: [],
        photo: "/images/segreteria.webp",
        testid: "team-segreteria",
    },
];
const TEAM = TEAM_ALL.filter((m) => m.testid !== "team-segreteria");

export default function ChiSiamo() {
    const [selected, setSelected] = useState(null);
    return (
        <>
            <section
                data-testid="chi-siamo-hero"
                className="bg-white pt-32 pb-16 sm:pt-40 md:pb-20"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <FadeUp y={14}>
                        <p className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            Chi Siamo · Dal 1990
                        </p>
                    </FadeUp>
                    <h1 className="mt-7 max-w-3xl font-serif text-4xl font-normal leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
                        <MaskedLine delay={0.15}>La tranquillità di sentirti</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="italic text-petrol">in buone mani.</span>
                        </MaskedLine>
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed tracking-wide text-body sm:text-lg">
                            Crediamo in un'odontoiatria fatta prima di tutto di
                            ascolto e chiarezza. Dalla prima visita al piano di
                            cura, il nostro impegno è offrirti risultati duraturi
                            in un ambiente sereno e riservato.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section
                data-testid="chi-siamo-dottore"
                className="bg-ice py-16 sm:py-20 md:py-28"
            >
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-8 md:px-12 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <FadeUp>
                            <ImageSlot
                                src={IMAGES.dottoreRitratto}
                                alt="Il Dr. Mario Massironi nel suo studio"
                                ratio="aspect-[4/5]"
                                testid="chi-siamo-doctor-image"
                            />
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-7 lg:pl-6">
                        <FadeUp y={16}>
                            <div className="flex flex-wrap gap-3">
                                {[
                                    { icon: GraduationCap, label: "Odontoiatra" },
                                    { icon: Wrench, label: "Odontotecnico" },
                                    { icon: HeartPulse, label: "Igienista Dentale" },
                                    { icon: MapPin, label: "Concorezzo (MB)" },
                                ].map((c) => (
                                    <span
                                        key={c.label}
                                        className="inline-flex items-center gap-2 rounded-full border border-petrol/30 bg-white px-4 py-2 text-xs font-normal uppercase tracking-[0.16em] text-petrol"
                                    >
                                        <c.icon className="h-3.5 w-3.5" />
                                        {c.label}
                                    </span>
                                ))}
                            </div>
                        </FadeUp>
                        <h2 className="mt-8 font-serif text-3xl font-normal leading-tight text-ink sm:text-4xl">
                            <MaskedLine delay={0.1}>Dr. Mario Massironi</MaskedLine>
                        </h2>
                        <FadeUp delay={0.2}>
                            <div className="mt-6 max-w-xl space-y-5 text-base font-light leading-relaxed tracking-wide text-body">
                                <p>
                                    Il Dr. Mario Massironi è odontoiatra,
                                    odontotecnico e igienista dentale: una
                                    combinazione di qualifiche rara, che unisce
                                    la competenza clinica, una profonda
                                    conoscenza dei materiali e delle soluzioni
                                    protesiche, e un'attenzione costante alla
                                    prevenzione quotidiana.
                                </p>
                                <p>
                                    Nel 1990 apre il suo studio a Concorezzo con
                                    l'obiettivo di offrire un percorso di cura
                                    completo, dalla prima visita alla consegna
                                    del lavoro finito. La sua doppia visione,
                                    clinica e tecnica, gli permette di valutare
                                    ogni dettaglio con estrema precisione e di
                                    garantire la massima qualità in ogni
                                    trattamento.
                                </p>
                                <p>
                                    La sua filosofia è semplice: un'agenda
                                    organizzata per dedicare a ciascuno il giusto
                                    tempo, ritmi distesi, spiegazioni chiare e
                                    preventivi sempre trasparenti.
                                </p>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.25}>
                            <p className="mt-9 text-[0.68rem] font-medium uppercase tracking-[0.24em] text-petrol">
                                Di cosa si occupa
                            </p>
                            <ul data-testid="mario-focus" className="mt-4 grid gap-2.5 sm:grid-cols-2">
                                {MARIO_FOCUS.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-ink">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                                        <span className="font-light">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {MARIO_SERVICES.map((id) => {
                                    const s = SERVICES.find((x) => x.id === id);
                                    return s ? (
                                        <Link
                                            key={id}
                                            to={`/servizi#${id}`}
                                            data-testid={`mario-service-${id}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-petrol/30 bg-white px-4 py-2 text-xs font-normal text-petrol transition-colors duration-300 hover:bg-petrol hover:text-white"
                                        >
                                            {s.name}
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </Link>
                                    ) : null;
                                })}
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.3}>
                            <Link
                                to="/contatti"
                                data-testid="chi-siamo-cta-prenota"
                                className="mt-10 inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                            >
                                <CalendarCheck className="h-4 w-4" />
                                Vieni a conoscerlo di persona
                            </Link>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <section
                data-testid="chi-siamo-team"
                className="bg-white py-16 sm:py-20 md:py-28"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <FadeUp y={14}>
                        <p className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            Il Team
                        </p>
                    </FadeUp>
                    <h2 className="mt-5 max-w-2xl font-serif text-3xl font-normal leading-tight text-ink sm:text-4xl">
                        <MaskedLine delay={0.1}>Le persone che ti curano.</MaskedLine>
                    </h2>
                    <Stagger
                        testid="chi-siamo-team-grid"
                        className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
                    >
                        {TEAM.map((t) => (
                            <StaggerItem key={t.testid}>
                                <button
                                    type="button"
                                    onClick={() => setSelected(t)}
                                    data-testid={t.testid}
                                    className="group flex h-full w-full flex-col overflow-hidden rounded-3xl border border-line bg-white text-left transition-all duration-500 hover:-translate-y-1.5 hover:border-petrol/40 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-petrol/50"
                                >
                                    {t.photo ? (
                                        <div className="relative aspect-[4/5] w-full overflow-hidden bg-ice">
                                            <img
                                                src={t.photo}
                                                alt={t.name}
                                                loading="lazy"
                                                className="h-full w-full object-cover grayscale transition-[transform,filter] duration-[1.2s] ease-[cubic-bezier(0.215,0.61,0.355,1)] group-hover:scale-[1.05] group-hover:grayscale-0"
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-4 border-b border-dashed border-petrol/30 bg-ice p-8 text-center">
                                            <ToothMark className="h-10 w-10 opacity-60" />
                                            <p className="text-[0.68rem] font-light uppercase tracking-[0.2em] text-muted">
                                                Foto in arrivo
                                            </p>
                                        </div>
                                    )}
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="font-serif text-xl font-medium leading-snug text-ink">
                                            {t.name}
                                        </h3>
                                        <p className="mt-1 text-[0.68rem] font-medium uppercase tracking-[0.2em] text-petrol">
                                            {t.role}
                                        </p>
                                        <p className="mt-3 text-sm font-light leading-relaxed tracking-wide text-body">
                                            {t.bio}
                                        </p>
                                        <span className="mt-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-petrol transition-colors duration-300 group-hover:text-petrol-dark">
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-petrol/40 transition-all duration-300 group-hover:rotate-90 group-hover:bg-petrol group-hover:text-white">
                                                <Plus className="h-3 w-3" />
                                            </span>
                                            Scopri di cosa si occupa
                                        </span>
                                    </div>
                                </button>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>
            <TeamMemberDialog member={selected} onClose={() => setSelected(null)} />
        </>
    );
}
